import React, { useState, useEffect, lazy, Suspense } from "react";
import { HashRouter as Router, Routes, Route } from "react-router-dom";
import TrainTable from "./components/TrainTable";
import TripPlanner from "./components/TripPlanner";
import Alert from "./components/Alert";
import Footer from "./components/Footer";
import "./App.css";
import { Helmet } from "react-helmet-async";
import stationsData from ".//data/stationsData.json";
import RailroadTracks from "./components/RailroadTracks";

import AIAlerts from "./components/AIAlerts";
import ServiceAlert from "./components/ServiceAlert";
import Navbar from "./components/Navbar";
import DataHealthPage from "./components/DataHealthPage";
import schedTypes from "./SchedTypes.js";
import {
  isIdleService,
  isLiveVehicleData,
  isLastKnownVehicleData,
  hasManagedFeedWarning,
  isScheduleOnly,
  effectiveFeedHealth,
  feedHealthMessage,
  selectDisplayTrains,
} from "./feedHealth";
const TrainCard = lazy(() => import("./components/TrainCard")); // Your station data

const App = () => {
  const [trains, setTrains] = useState([]);
  const [updated, setUpdated] = useState("");
  const [feedHealth, setFeedHealth] = useState(null);

  useEffect(() => {
    let latestRequestId = 0;
    let disposed = false;

    const fetchData = () => {
      const requestId = ++latestRequestId;
      fetch("https://caltrain.live/data.json")
        .then((response) => {
          if (!response.ok) throw new Error("Network response was not OK");
          return response.json(); // Directly parse JSON here
        })
        .then((data) => {
          if (disposed || requestId !== latestRequestId) return;
          const resolvedFeedHealth = effectiveFeedHealth(data);
          setTrains(selectDisplayTrains({
            ...data,
            feedHealth: resolvedFeedHealth,
          }));
          setUpdated(typeof data.updated === "string" ? data.updated : "");
          setFeedHealth(resolvedFeedHealth);
        })
        .catch((error) => {
          console.error("Fetching error:", error);
          if (disposed || requestId !== latestRequestId) return;
          // Never keep drawing stale vehicles after a failed refresh.
          setTrains([]);
          setFeedHealth({
            status: "unavailable",
            reason: "website_fetch_failed",
          });
        });
    };

    fetchData();
    const interval = setInterval(fetchData, 60000); // Refresh every minute
    return () => {
      disposed = true;
      clearInterval(interval);
    };
  }, []);

  return (
    <div className="app-shell">
      <Helmet>
        <meta
          name="apple-itunes-app"
          content="app-id=6737343619, app-argument=caltrainlive"
        />
      </Helmet>
      <Router>
        <Navbar feedHealth={feedHealth} />
        <Routes>
          <Route
            path="/"
            element={
              <Home
                trains={trains}
                updated={updated}
                feedHealth={feedHealth}
              />
            }
          />
          <Route
            path="/:trainId"
            element={
              isLiveVehicleData(feedHealth) || isScheduleOnly(feedHealth) ? (
                <Suspense fallback={<div className="page-loading">Loading train details…</div>}>
                  <TrainCard trains={trains} />
                </Suspense>
              ) : (
                <main className="page-loading" role="status">
                  Live train details are temporarily unavailable.
                </main>
              )
            }
          />
          <Route
            path="/planner/:originId?/:destinationId?"
            element={<TripPlanner />}
          />
          <Route
            path="/health"
            element={<DataHealthPage feedHealth={feedHealth} />}
          />
        </Routes>
      </Router>
    </div>
  );
};

export const Home = ({ trains, updated, feedHealth }) => {
  const [showTracks, setShowTracks] = useState(false); // Toggle for mobile
  const northboundStops = stationsData.filter(
    (station) =>
      station.scheduleTypes.includes(schedTypes.local) ||
      station.scheduleTypes.includes(schedTypes.limited) ||
      station.scheduleTypes.includes(schedTypes.express) ||
      station.scheduleTypes.includes(schedTypes.southcounty)
  );
  const southboundStops = [...northboundStops].reverse(); // Reverse for southbound
  const isMobile = window.innerWidth <= 768;
  const isAppleDevice = /Macintosh|MacIntel|iPhone|iPad|iPod/.test(
    navigator.userAgent
  );
  const liveVehicleData = isLiveVehicleData(feedHealth);
  const scheduleOnly = isScheduleOnly(feedHealth);
  const lastKnownVehicleData = isLastKnownVehicleData(feedHealth);
  const idleService = isIdleService(feedHealth);

  return (
    <main className="home-page">
      <section className="live-section" id="live-trains">
        {(scheduleOnly || (liveVehicleData && feedHealth?.status !== "healthy" && feedHealth?.warning)) && (
          <div className="feed-health-banner" role="status">
            {feedHealthMessage(feedHealth)}
          </div>
        )}
        <div className="section-heading">
          <div>
            <p className="eyebrow">System overview</p>
            <h2>
              {scheduleOnly
                ? lastKnownVehicleData
                  ? "Train locations may be delayed"
                  : "Scheduled trains"
                : idleService
                  ? "No trains operating now"
                  : "Trains moving now"}
            </h2>
          </div>
          <p>
            {liveVehicleData
              ? "Choose a train to see its route and upcoming stops."
              : idleService
                ? "No current service is scheduled."
                : lastKnownVehicleData
                  ? "Showing the last verified positions while waiting for the Caltrain location API to recover."
                  : "Live positions and delays are temporarily unavailable."}
          </p>
        </div>

        {isMobile && liveVehicleData && (
          <button
            className="show-tracks-button"
            onClick={() => setShowTracks(!showTracks)}
          >
            {showTracks ? "Show train list" : "Show track map"}
          </button>
        )}

        <div className="home-container">
          {(!isMobile || showTracks) && (
            <aside className="track-container-wrapper">
              <div className="track-card-heading">
                <span>Corridor map</span>
                <span className="track-card-direction">San Francisco → Gilroy</span>
              </div>
              <RailroadTracks
                stops={southboundStops}
                trains={liveVehicleData ? trains : []}
                direction="Southbound"
              />
            </aside>
          )}

          {(!isMobile || !showTracks) && (
            <div className="table-container">
              <AIAlerts />
              <ServiceAlert
                hideVehicleFeedOutage={hasManagedFeedWarning(feedHealth)}
              />

              {trains.some((t) => t.canceled) && (
                <TrainTable
                  direction="Canceled"
                  trains={trains.filter((t) => t.canceled)}
                />
              )}
              <TrainTable
                direction="Northbound"
                trains={trains.filter((t) => t.direction === "N" && !t.canceled)}
              />
              <TrainTable
                direction="Southbound"
                trains={trains.filter((t) => t.direction === "S" && !t.canceled)}
              />
              {isAppleDevice && <Alert />}
            </div>
          )}
        </div>
      </section>
      <Footer updated={updated} />
    </main>
  );
};

export default App;
